import React from 'react';
import { CheckCircle, AlertCircle, XCircle } from 'lucide-react';
import { QuizResponse } from './types';

// Mapping des questions
const questionsMap: Record<string, string> = {
  '1': 'Quel est votre taux de conversion moyen sur votre site ?',
  '2': 'Quel est votre plus grand défi en marketing digital actuellement ?',
  '3': 'Avez-vous déjà testé l\'email marketing pour votre entreprise ?',
  '4': 'Utilisez-vous un CRM pour suivre vos prospects ?',
  '5': 'Mesurez-vous le ROI de vos campagnes marketing ?',
  '6': 'Quelle est votre stratégie actuelle d\'acquisition de clients ?',
  '7': 'Quel est votre objectif principal pour les 6 prochains mois ?'
};

interface QuizResponsesCardProps {
  quizResponses: QuizResponse[];
}

const QuizResponsesCard: React.FC<QuizResponsesCardProps> = ({ quizResponses }) => {
  // Normaliser le score sur 4
  const normalizeScore = (score: number): number => {
    // Considérons que le score_partiel max est 30 (basé sur les données de la BDD)
    const maxScore = 30;
    return Math.round((score / maxScore) * 4 * 10) / 10;
  };

  const getResponseIcon = (score: number) => {
    const normalizedScore = normalizeScore(score);
    if (normalizedScore >= 3) return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (normalizedScore >= 2) return <AlertCircle className="w-4 h-4 text-yellow-500" />;
    return <XCircle className="w-4 h-4 text-red-500" />;
  };

  const getResponseColor = (score: number) => {
    const normalizedScore = normalizeScore(score);
    if (normalizedScore >= 3) return 'bg-green-50 border-green-200';
    if (normalizedScore >= 2) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  // Calculer le score moyen normalisé
  const calculateAverageScore = () => {
    if (quizResponses.length === 0) return 0;
    const totalScore = quizResponses.reduce((sum, r) => sum + normalizeScore(r.score_partiel), 0);
    return (totalScore / quizResponses.length).toFixed(1);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">
          Quiz ({quizResponses.length} réponses)
        </span>
        {quizResponses.length > 0 && (
          <span className="text-xs text-gray-500">
            Score moyen: {calculateAverageScore()}/4
          </span>
        )}
      </div>

      {quizResponses.length === 0 ? (
        <div className="text-sm text-gray-500 italic">Aucune réponse au quiz</div>
      ) : (
        <div className="space-y-2">
          {quizResponses.map((response) => (
            <div
              key={response.id}
              className={`p-3 rounded-md border-l-4 ${getResponseColor(response.score_partiel)}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  {getResponseIcon(response.score_partiel)}
                  <span className="ml-2 text-xs font-medium text-gray-600">
                    Q{response.question_id}
                  </span>
                </div>
                <span className="text-xs text-gray-500">
                  {normalizeScore(response.score_partiel)}/4
                </span>
              </div>
              <div className="text-xs text-gray-700">
                <p className="font-medium mb-1">{questionsMap[response.question_id] || `Question ${response.question_id}`}</p>
                <p className="text-gray-600">{response.reponse}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuizResponsesCard;